import {
  init,
  track,
  reset,
  setUserId,
  getUserId,
  Identify,
  identify
} from '@amplitude/analytics-browser'
import {
  PromoNextButtonEvent,
  PromoScreenOpenedEvent
} from '../components/ApplicationProgress/PromoCode/types'
import {
  AdditionalReviewScreen,
  CreditApplicationErrors,
  NotApprovedScreen,
  OopsErrorScreen
} from '../components/ApplicationProgress/ApplicationSubmitted/types'
import {
  EventCategory,
  EventType,
  TrackingEvent,
  ButtonTracking,
  ScreenTracking,
  NavigationTracking,
  PromocodeTracking,
  TrackingEvents,
  USER_PROPERTIES
} from '../domain/amplitude'
import { logger } from './logger'
import {
  AddressNextButton,
  AddressScreenOpened
} from '../components/ApplicationProgress/Address/types'
import {
  PersonalScreenNextButton,
  PersonalScreenOpened
} from '../components/ApplicationProgress/PersonalInformation/types'
import { AlmostScreenOpened } from '../components/ApplicationProgress/AlmostThere/types'
import { AppConfig } from 'domain/config'
import { User } from 'domain/user'

/**
 * Access the SDK anywhere after it's initialized.
 */
export const initialize = (config: AppConfig): void => {
  if (config.variables.AMPLITUDE_API_KEY) {
    init(config.variables.AMPLITUDE_API_KEY)
  }
}

export const setIdentity = (user: User): void => {
  setUserId(user.primaryEmail)
  const identifyObj = new Identify()
  identifyObj.set(USER_PROPERTIES.EXTERNAL_ID, user.externalId)
  identify(identifyObj)
}

export const getAmplitudeUserId = (): string | undefined => getUserId()

export const sendTrackingEvent = (
  event: TrackingEvent<TrackingEvents>
): void => {
  track(
    { event_type: event.event_type, user_id: getAmplitudeUserId() },
    { ...event.event_properties }
  )
    .promise.then((result) => logger.debug(`Amplitude sendTrackingEvent: ${result.message}`))
    .catch((err) => logger.warn(`Amplitude sendTrackingEvent Error: ${err.message}`))
}

export const toButtonEvent = (
  props: ButtonTracking
): TrackingEvent<ButtonTracking> => {
  return {
    event_type: EventType.BUTTON_PRESSED,
    event_properties: {
      asset_id: props.asset_id,
      asset_text: props.asset_text,
      event_category: EventCategory.BUTTON,
      screen: props.screen
    }
  }
}

export const toValidatedPromocodeEvent = (
  props: PromocodeTracking
): TrackingEvent<PromocodeTracking> => {
  return {
    event_type: EventType.VALIDATED_PROMOCODE,
    event_properties: {
      code_used: props.code_used
    }
  }
}

export const toNavigationEvent = (
  props: NavigationTracking
): TrackingEvent<NavigationTracking> => {
  return {
    event_type: EventType.NAVIGATION_PRESSED,
    event_properties: {
      asset_id: props.asset_id,
      asset_text: props.asset_text,
      event_category: EventCategory.NAVIGATION,
      screen: props.screen
    }
  }
}

export const toScreenEvent = (
  props: ScreenTracking
): TrackingEvent<ScreenTracking> => {
  return {
    event_type: EventType.SCREEN_OPENED,
    event_properties: {
      asset_id: props.asset_id,
      heading: props.heading,
      from_screen: props.from_screen,
      event_category: EventCategory.SCREEN,
      screen: props.screen
    }
  }
}

export const onLogout = (): void => {
  reset()
}

export const getScreenEventForError = (
  type: CreditApplicationErrors
): ScreenTracking => {
  switch (type) {
    case CreditApplicationErrors.REVIEW:
      return AdditionalReviewScreen
    case CreditApplicationErrors.NOT_APPROVED:
      return NotApprovedScreen
    case CreditApplicationErrors.GENERIC:
    case CreditApplicationErrors.HTTP:
      return OopsErrorScreen
    default:
      return OopsErrorScreen
  }
}

export const generatePromotionScreenEvent = (): void => {
  sendTrackingEvent(toScreenEvent({ ...PromoScreenOpenedEvent }))
}

export const generatePromotionNextButtonEvent = (): void => {
  sendTrackingEvent(toButtonEvent({ ...PromoNextButtonEvent }))
}

export const generateValidatedPromotionEvent = (code: string): void => {
  sendTrackingEvent(toValidatedPromocodeEvent({ code_used: code }))
}

export const generateAddressScreenEvent = (): void => {
  sendTrackingEvent(toScreenEvent({ ...AddressScreenOpened }))
}

export const generateAddressNextButtonEvent = (): void => {
  sendTrackingEvent(toButtonEvent({ ...AddressNextButton }))
}

export const generatePersonalInformationScreenEvent = (): void => {
  sendTrackingEvent(toScreenEvent({ ...PersonalScreenOpened }))
}

export const generatePersonalInformatButtonEvent = (): void => {
  sendTrackingEvent(toButtonEvent({ ...PersonalScreenNextButton }))
}

export const generateAlmostThereScreenEvent = (): void => {
  sendTrackingEvent(toScreenEvent({ ...AlmostScreenOpened }))
}
